import React, { useState } from "react";
import { Modal, message } from "antd";
import Table from "../../../shared/dataTable/index";
import { LeaveDto, LeaveStatus } from "../../../../types/leaves.types";
import dayjs from "dayjs";
import LoadingSpinner from "../../../shared/uiInterface/base-ui/LoadingSpinner";
import { getFileUrl } from "../../../../utils/file.utils";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FileService from "../../../../services/file/file.service";
import { Popover, Spin, Dropdown, Menu } from 'antd';
import LeaveStatusDropdown from './LeaveStatusDropdown';
import { PermissionType } from '../../../../types/user.types';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { exportLeaveDetailsToPdf } from '../../../../utils/exportUtils';
import { useCompanyData } from "../../../../hooks/useCompanyData";

interface EmployeeLeaveTableProps {
  leaves: LeaveDto[];
  loading: boolean;
  onEdit: (leave: LeaveDto) => void;
  onDelete: (id: number) => void;
  onCancel?: (id: number) => void;
}

export const EmployeeLeaveTable: React.FC<EmployeeLeaveTableProps> = ({
  leaves,
  loading,
  onEdit,
  onDelete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { companyData } = useCompanyData();
  const fileService = FileService.getInstance();
  const [popoverVisible, setPopoverVisible] = useState<Record<string, boolean>>({});
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState<boolean>(false);
  const { user: currentUser } = useSelector((state: RootState) => state.auth);

  // Verileri updatedAt'e göre sırala (en son güncellenen en üstte)
  const sortedLeaves = [...leaves].sort((a, b) => {
    const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return dateB - dateA; // Azalan sırada sıralama (en yeni en üstte)
  });

  const showCancelConfirm = (leaveId: number) => {
    Modal.confirm({
      title: "İzin Talebini İptal Et",
      content:
        "Bu izin talebini iptal etmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      okText: "Evet, İptal Et",
      cancelText: "Vazgeç",
      okType: "danger",
      onOk() {
        if (onCancel) {
          onCancel(leaveId);
        }
      },
    });
  };

  const columns = [
    {
      title: t("leaveEmployee.leaveType"),
      dataIndex: "type",
      render: (_: string, record: LeaveDto) => (
        <div className="d-flex align-items-center">
          <p className="fs-14 fw-medium mb-0">
            {t(`leaveTypes.${record.type}`)}
          </p>
          {record.reason && (
            <span
              className="ms-2"
              data-bs-toggle="tooltip"
              data-bs-placement="right"
              title={record.reason}
            >
              <i className="ti ti-info-circle text-info" />
            </span>
          )}
        </div>
      ),
    },
    {
      title: t("leaveEmployee.date"),
      dataIndex: "startDate",
      render: (_: string, record: LeaveDto) =>
        record.type === "hourly" ? (
          <p className="fs-14 mb-0">
            {dayjs(record.startDate).format("DD.MM.YYYY")}
            <br />
            <small className="text-muted">
              {record.startTime} - {record.endTime}
            </small>
          </p>
        ) : (
          <p className="fs-14 mb-0">
            {dayjs(record.startDate).format("DD.MM.YYYY")} -
            <br />
            {dayjs(record.endDate).format("DD.MM.YYYY")}
          </p>
        ),
    },
    {
      title: t("leaveEmployee.statusInfo"),
      dataIndex: "status",
      render: (_: string, record: LeaveDto) => (
        <div className="d-inline-flex align-items-center">
          <span
            className={`rounded-circle ${
              record.status === "approved"
                ? "bg-transparent-success"
                : record.status === "pending"
                ? "bg-transparent-purple"
                : "bg-transparent-danger"
            } d-flex justify-content-center align-items-center me-2`}
          >
            <i
              className={`ti ti-point-filled ${
                record.status === "approved"
                  ? "text-success"
                  : record.status === "pending"
                  ? "text-purple"
                  : "text-danger"
              }`}
            />
          </span>
          {record.status === "approved"
            ? t("leaves.statusOptions.approved")
            : record.status === "pending"
            ? t("leaves.statusOptions.pending")
            : t("leaves.statusOptions.rejected")}
          {/* {record.approvedBy && (
            <small className="text-muted ms-2">({record.approvedBy})</small>
          )} */}
        </div>
      ),
    },
    {
      title: t("leaveEmployee.description"),
      dataIndex: "reason",
      render: (reason: string) =>
        reason.length > 30 ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip title={reason} placement="top">
                {reason}
              </Tooltip>
            }
          >
            <p
              className="fs-14 mb-0"
              style={{
                maxWidth: "200px",
                whiteSpace: "nowrap",
                overflow: "auto",
                scrollbarWidth: "none",
                position: "relative",
              }}
            >
              {reason}
              {reason.length > 30 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    width: "20px",
                    height: "100%",
                  }}
                />
              )}
            </p>
          </OverlayTrigger>
        ) : (
          <p className="fs-14 mb-0">{reason}</p>
        ),
    },
    {
      title: "İşlemi Yapan",
      dataIndex: "status",
      render: (status: string, record: any) => {
        if (status === "approved" && record.approvedBy) {
          return (
            <p className="fs-14 mb-0">
              {record.approvedBy.firstName} {record.approvedBy.lastName}
            </p>
          );
        } else if (status === "rejected" && record.rejectedBy) {
          return (
            <p className="fs-14 mb-0">
              {record.rejectedBy.firstName} {record.rejectedBy.lastName}
            </p>
          );
        } else {
          return <p className="fs-14 mb-0">-</p>;
        }
      },
    },
    {
      title: t("leaveEmployee.lastUpdate"),
      dataIndex: "updatedAt",
      render: (_: string, record: LeaveDto) => (
        <p className="fs-14 mb-0">
          {record.updatedAt
            ? dayjs(record.updatedAt).format("DD.MM.YYYY HH:mm")
            : "-"}
        </p>
      ),
      sorter: (a: LeaveDto, b: LeaveDto) => {
        const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return dateA - dateB;
      },
      defaultSortOrder: "descend",
    },
    {
      title: t("leaveEmployee.file"),
      dataIndex: "file",
      render: (_: string, record: LeaveDto) => (
        <p className="fs-14 mb-0">
          {record.documentUrl ? (
            <a
              className="dropdown-item"
              href="#"
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await FileService.getInstance().viewFileAsBlob(record.documentUrl!);
                } catch (error) {
                  message.error('Belge görüntülenirken hata oluştu');
                }
              }}
            >
              {t("leaveEmployee.view")}
            </a>
          ) : (
            "-"
          )}
        </p>
      ),
    },

    {
      title: "",
      key: "actions",
      render: (_: string, record: LeaveDto) => {
        const isEditable =
          record.status === LeaveStatus.PENDING ||
          record.status === LeaveStatus.PENDING_HIERARCHY_APPROVAL;
        // Onaylanmış ama henüz başlamamış izinler de iptal edilebilir (bkz. backend
        // cancelOwnLeave: allowedStatuses). Düzenleme ise backend'de sadece pending/
        // pending_hierarchy_approval için izinli, o yüzden "Düzenle" ayrı koşulda.
        const isCancellable = isEditable || record.status === LeaveStatus.APPROVED;

        return (
          isCancellable && (
          <div className="d-flex gap-2">
            <Dropdown
              overlay={
                <Menu>
                  {isEditable && (
                    <Menu.Item key="edit" onClick={() => onEdit(record)}>
                      {t("leaveEmployee.edit")}
                    </Menu.Item>
                  )}
                  <Menu.Item key="pdf" onClick={async (e) => {
                    e.domEvent.preventDefault();
                    Modal.confirm({
                      title: 'PDF İndir',
                      content: 'İzin detaylarını PDF olarak indirmek istediğinizden emin misiniz?',
                      onOk: async () => {
                        await exportLeaveDetailsToPdf(record, companyData?.name);
                      },
                      okText: 'İndir',
                      cancelText: 'İptal',
                    });
                  }}>
                    PDF İndir
                  </Menu.Item>
                  <Menu.Item key="cancel" onClick={() => onCancel && onCancel(record.id)}>
                    {t("leaveEmployee.cancelRequest")}
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <button className="btn btn-sm btn-primary">
                <i className="ti ti-dots-vertical" />
              </button>
            </Dropdown>
          </div>
          )
        );
      },
    },
  ];

  if (loading) {
    return <LoadingSpinner size="lg" color="primary" centered />;
  }

  return (
    <Table dataSource={sortedLeaves} columns={columns} Selection={false} />
  );
};

export default EmployeeLeaveTable;
